'use memo';

import { AutopilotTaskDto } from '@urbanx/agx.activities.client';
import {
  Condition,
  Deposit,
  DepositType,
  FormCondition,
  StaffUpdateContractComplianceFieldsRequest,
  StaffUpdateContractDatesRequest,
  StaffUpdateContractSettlementRequest,
  SpecialConditionData,
  WaiverOfCoolingOff,
  useGetCampaign,
  useGetSpecialConditionBank,
  useInvalidateUseStaffGetContractDates,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContractSettlement,
  useInvalidateUseStaffGetSaleInformation,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetContractDeposits,
  useStaffGetContractDates,
  useStaffGetContractSettlement,
  useStaffGetSaleInformation,
  useStaffGetSpecialConditions,
  useStaffUpdateContractComplianceFields,
  useStaffUpdateContractDates,
  useStaffUpdateContractDeposits,
  useStaffUpdateContractSettlement,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client';
import { isEqual } from 'lodash-es';
import TaskModalDialog, {
  type TaskModalContentConfig,
} from 'pages/allTasks/TaskModal/TaskModalDialog';
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps';
import { useEffect, useRef, useState } from 'react';
import ContractCompliancePage from './ContractCompliancePage';
import { getContractComplianceDocumentGroups } from './ContractComplianceHelper';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';
import { prepareCustomSpecialConditionsForSave } from '../contract-information/modals/specialConditions/customSpecialConditionEditor';

const toComplianceContractDateRequest = (
  value?: Date
): StaffUpdateContractComplianceFieldsRequest['contractDate'] =>
  toDateOnlyString(value) as StaffUpdateContractComplianceFieldsRequest['contractDate'];

const toContractDatesContractDateRequest = (
  value?: Date
): StaffUpdateContractDatesRequest['contractDate'] =>
  toDateOnlyString(value) as StaffUpdateContractDatesRequest['contractDate'];

const sanitizeFormConditionSpecialConditionReference = (
  formCondition: FormCondition | undefined,
  validSpecialConditionIds: Set<string>
) => {
  if (
    !formCondition ||
    formCondition.condition !== Condition.UponSatisfactionOfSpecialCondition ||
    !formCondition.specialConditionId ||
    validSpecialConditionIds.has(formCondition.specialConditionId)
  ) {
    return formCondition;
  }

  return {
    ...formCondition,
    specialConditionId: undefined,
  };
};

const sanitizeDepositSpecialConditionReference = (
  deposit: Deposit | undefined,
  validSpecialConditionIds: Set<string>
) => {
  if (
    !deposit ||
    deposit.condition !== Condition.UponSatisfactionOfSpecialCondition ||
    !deposit.specialConditionId ||
    validSpecialConditionIds.has(deposit.specialConditionId)
  ) {
    return deposit;
  }

  return {
    ...deposit,
    specialConditionId: undefined,
  };
};

interface ContractComplianceModalProps {
  campaignId: string;
  isOpen: boolean;
  onClose: () => void;
  onCompleteSuccess?: () => void;
  tasks: AutopilotTaskDto[];
}

const ContractComplianceModal = ({
  campaignId,
  isOpen,
  onClose,
  onCompleteSuccess,
  tasks,
}: ContractComplianceModalProps) => {
  const contractId = tasks.find((task) => task.contractId)?.contractId ?? '';

  const { data: campaign, isPending: isCampaignPending } = useGetCampaign(campaignId, !!campaignId);

  const selectedContract = campaign?.contractDetails?.find(
    (contract) => contract.contractId === contractId
  );

  const filteredDocuments = getContractComplianceDocumentGroups(selectedContract);

  const { data: contractDates, isPending: contractDatesPending } = useStaffGetContractDates(
    campaignId,
    contractId,
    !!contractId && isOpen
  );
  const { data: contractSettlement, isPending: contractSettlementPending } =
    useStaffGetContractSettlement(campaignId, contractId, !!contractId && isOpen);
  const { data: contractDeposits, isPending: contractDepositsPending } =
    useStaffGetContractDeposits(campaignId, contractId, !!contractId && isOpen);
  const { data: saleInformation, isPending: saleInformationPending } = useStaffGetSaleInformation(
    campaignId,
    contractId,
    !!contractId && isOpen
  );
  const { data: specialConditionsResponse, isPending: specialConditionsPending } =
    useStaffGetSpecialConditions(campaignId, contractId, !!campaignId && !!contractId && isOpen);
  const { isPending: specialConditionBankPending } = useGetSpecialConditionBank(isOpen);

  const [contractDate, setContractDate] = useState<Date | undefined>();
  const [contractDateDirty, setContractDateDirty] = useState(false);
  const [salePrice, setSalePrice] = useState<number | undefined>();
  const [salePriceDirty, setSalePriceDirty] = useState(false);
  const [initialDeposit, setInitialDeposit] = useState<Deposit | undefined>();
  const [balanceDeposit, setBalanceDeposit] = useState<Deposit | undefined>();
  const [depositsDirty, setDepositsDirty] = useState(false);
  // const [depositValidationError, setDepositValidationError] = useState(false)
  // const [financeConditionValidationError, setFinanceConditionValidationError] =
  //   useState(false)
  // const [buildingAndPestValidationError, setBuildingAndPestValidationError] =
  //   useState(false)
  // const [
  //   bodyCorporateRecordsValidationError,
  //   setBodyCorporateRecordsValidationError,
  // ] = useState(false)
  // const [settlementDateValidationError, setSettlementDateValidationError] =
  //   useState(false)
  const [subjectToFinance, setSubjectToFinance] = useState<FormCondition | undefined>();
  const [subjectToFinanceDirty, setSubjectToFinanceDirty] = useState(false);
  const [waiverOfCoolingOff, setWaiverOfCoolingOff] = useState<WaiverOfCoolingOff | undefined>();
  const [waiverOfCoolingOffDirty, setWaiverOfCoolingOffDirty] = useState(false);
  const [buildingAndPestCondition, setBuildingAndPestCondition] = useState<
    FormCondition | undefined
  >();
  const [buildingAndPestConditionDirty, setBuildingAndPestConditionDirty] = useState(false);
  const [bodyCorporateRecordsCondition, setBodyCorporateRecordsCondition] = useState<
    FormCondition | undefined
  >();
  const [bodyCorporateRecordsConditionDirty, setBodyCorporateRecordsConditionDirty] =
    useState(false);
  const [settlementInfo, setSettlementInfo] = useState<FormCondition | undefined>();
  const [settlementInfoDirty, setSettlementInfoDirty] = useState(false);
  const [specialConditions, setSpecialConditions] = useState<SpecialConditionData[]>([]);
  const [specialConditionsDirty, setSpecialConditionsDirty] = useState(false);
  const specialConditionsRef = useRef<SpecialConditionData[]>([]);
  const specialConditionsDirtyRef = useRef(false);
  const [hasSpecialConditions, setHasSpecialConditions] = useState(false);

  const originalInitialDeposit = contractDeposits?.deposits?.find(
    (deposit) => deposit.depositType === DepositType.Initial
  );
  const originalBalanceDeposit = contractDeposits?.deposits?.find(
    (deposit) => deposit.depositType === DepositType.Balance
  );

  useEffect(() => {
    if (!contractDateDirty)
      setContractDate(dateOnlyToDate(contractDates?.contractDate) ?? undefined);
  }, [contractDateDirty, contractDates?.contractDate]);

  useEffect(() => {
    if (!salePriceDirty) setSalePrice(saleInformation?.salePrice ?? undefined);
  }, [saleInformation?.salePrice, salePriceDirty]);

  useEffect(() => {
    if (!subjectToFinanceDirty) {
      setSubjectToFinance(contractDates?.subjectToFinance);
    }
  }, [contractDates?.subjectToFinance, subjectToFinanceDirty]);

  useEffect(() => {
    if (!waiverOfCoolingOffDirty) {
      setWaiverOfCoolingOff(contractDates?.waiverOfCoolingOff);
    }
  }, [contractDates?.waiverOfCoolingOff, waiverOfCoolingOffDirty]);

  useEffect(() => {
    if (!buildingAndPestConditionDirty) {
      setBuildingAndPestCondition(contractDates?.buildingAndPestCondition);
    }
  }, [buildingAndPestConditionDirty, contractDates?.buildingAndPestCondition]);

  useEffect(() => {
    if (!bodyCorporateRecordsConditionDirty) {
      setBodyCorporateRecordsCondition(contractDates?.bodyCorporateRecordsCondition);
    }
  }, [bodyCorporateRecordsConditionDirty, contractDates?.bodyCorporateRecordsCondition]);

  useEffect(() => {
    if (!settlementInfoDirty) {
      setSettlementInfo(contractSettlement?.settlementInfo);
    }
  }, [contractSettlement?.settlementInfo, settlementInfoDirty]);

  useEffect(() => {
    if (depositsDirty) return;

    setInitialDeposit(originalInitialDeposit);
    setBalanceDeposit(originalBalanceDeposit);
  }, [depositsDirty, originalBalanceDeposit, originalInitialDeposit]);

  useEffect(() => {
    if (specialConditionsDirty) return;

    const responseSpecialConditions = specialConditionsResponse?.specialConditionsData ?? [];

    setSpecialConditions(responseSpecialConditions);
    specialConditionsRef.current = responseSpecialConditions;
    setHasSpecialConditions(responseSpecialConditions.length > 0);
    specialConditionsDirtyRef.current = false;
  }, [specialConditionsDirty, specialConditionsResponse?.specialConditionsData]);

  const invalidateContractDates = useInvalidateUseStaffGetContractDates();
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const invalidateContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const invalidateSaleInformation = useInvalidateUseStaffGetSaleInformation();
  const invalidateSpecialConditions = useInvalidateUseStaffGetSpecialConditions();

  const { staffUpdateContractComplianceFields } = useStaffUpdateContractComplianceFields({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractDates } = useStaffUpdateContractDates({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractSpecialConditions } = useStaffUpdateContractSpecialConditions({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractDeposits } = useStaffUpdateContractDeposits({
    onSuccess: () => {},
    onError: () => {},
  });

  const onContractDateUpdated = (updatedContractDate: Date | undefined) => {
    setContractDate(updatedContractDate);
    setContractDateDirty(
      dateOnlyToDate(contractDates?.contractDate)?.toString() !== updatedContractDate?.toString()
    );
  };

  const onSalePriceUpdated = (updatedSalePrice: number | undefined) => {
    setSalePrice(updatedSalePrice);
    setSalePriceDirty((saleInformation?.salePrice ?? undefined) !== updatedSalePrice);
  };

  const onSpecialConditionsUpdated = (updatedSpecialConditions: SpecialConditionData[]) => {
    const preparedSpecialConditions =
      prepareCustomSpecialConditionsForSave(updatedSpecialConditions);
    const validSpecialConditionIds = new Set(
      preparedSpecialConditions
        .map((condition) => condition.id)
        .filter((conditionId): conditionId is string => Boolean(conditionId))
    );

    const nextSubjectToFinance = sanitizeFormConditionSpecialConditionReference(
      subjectToFinance,
      validSpecialConditionIds
    );
    const nextBuildingAndPestCondition = sanitizeFormConditionSpecialConditionReference(
      buildingAndPestCondition,
      validSpecialConditionIds
    );
    const nextBodyCorporateRecordsCondition = sanitizeFormConditionSpecialConditionReference(
      bodyCorporateRecordsCondition,
      validSpecialConditionIds
    );
    const nextSettlementInfo = sanitizeFormConditionSpecialConditionReference(
      settlementInfo,
      validSpecialConditionIds
    );
    const nextInitialDeposit = sanitizeDepositSpecialConditionReference(
      initialDeposit,
      validSpecialConditionIds
    );
    const nextBalanceDeposit = sanitizeDepositSpecialConditionReference(
      balanceDeposit,
      validSpecialConditionIds
    );
    const isSpecialConditionsDirty = !isEqual(
      specialConditionsResponse?.specialConditionsData ?? [],
      preparedSpecialConditions
    );

    setSpecialConditions(preparedSpecialConditions);
    specialConditionsRef.current = preparedSpecialConditions;
    setHasSpecialConditions(preparedSpecialConditions.length > 0);
    setSubjectToFinance(nextSubjectToFinance);
    setBuildingAndPestCondition(nextBuildingAndPestCondition);
    setBodyCorporateRecordsCondition(nextBodyCorporateRecordsCondition);
    setSettlementInfo(nextSettlementInfo);
    setInitialDeposit(nextInitialDeposit);
    setBalanceDeposit(nextBalanceDeposit);
    setSpecialConditionsDirty(isSpecialConditionsDirty);
    specialConditionsDirtyRef.current = isSpecialConditionsDirty;
    setSubjectToFinanceDirty(!isEqual(contractDates?.subjectToFinance, nextSubjectToFinance));
    setBuildingAndPestConditionDirty(
      !isEqual(contractDates?.buildingAndPestCondition, nextBuildingAndPestCondition)
    );
    setBodyCorporateRecordsConditionDirty(
      !isEqual(contractDates?.bodyCorporateRecordsCondition, nextBodyCorporateRecordsCondition)
    );
    setSettlementInfoDirty(!isEqual(contractSettlement?.settlementInfo, nextSettlementInfo));
    setDepositsDirty(
      !isEqual(originalInitialDeposit, nextInitialDeposit) ||
        !isEqual(originalBalanceDeposit, nextBalanceDeposit)
    );
    return true;
  };

  const onDepositsUpdated = (updated: { initialDeposit?: Deposit; balanceDeposit?: Deposit }) => {
    setInitialDeposit(updated.initialDeposit);
    setBalanceDeposit(updated.balanceDeposit);
    setDepositsDirty(
      !isEqual(originalInitialDeposit, updated.initialDeposit) ||
        !isEqual(originalBalanceDeposit, updated.balanceDeposit)
    );
  };

  const onSubjectToFinanceUpdated = (updatedSubjectToFinance: FormCondition | undefined) => {
    setSubjectToFinance(updatedSubjectToFinance);
    setSubjectToFinanceDirty(!isEqual(contractDates?.subjectToFinance, updatedSubjectToFinance));
  };

  const onWaiverOfCoolingOffUpdated = (
    updatedWaiverOfCoolingOff: WaiverOfCoolingOff | undefined
  ) => {
    setWaiverOfCoolingOff(updatedWaiverOfCoolingOff);
    setWaiverOfCoolingOffDirty(
      !isEqual(contractDates?.waiverOfCoolingOff, updatedWaiverOfCoolingOff)
    );
  };

  const onBuildingAndPestConditionUpdated = (
    updatedBuildingAndPestCondition: FormCondition | undefined
  ) => {
    setBuildingAndPestCondition(updatedBuildingAndPestCondition);
    setBuildingAndPestConditionDirty(
      !isEqual(contractDates?.buildingAndPestCondition, updatedBuildingAndPestCondition)
    );
  };

  const onBodyCorporateRecordsConditionUpdated = (
    updatedBodyCorporateRecordsCondition: FormCondition | undefined
  ) => {
    setBodyCorporateRecordsCondition(updatedBodyCorporateRecordsCondition);
    setBodyCorporateRecordsConditionDirty(
      !isEqual(contractDates?.bodyCorporateRecordsCondition, updatedBodyCorporateRecordsCondition)
    );
  };

  const onSettlementInfoUpdated = (updatedSettlementInfo: FormCondition | undefined) => {
    setSettlementInfo(updatedSettlementInfo);
    setSettlementInfoDirty(!isEqual(contractSettlement?.settlementInfo, updatedSettlementInfo));
  };

  const onHasSpecialConditionsUpdated = (updatedHasSpecialConditions: boolean) => {
    setHasSpecialConditions(updatedHasSpecialConditions);

    if (!updatedHasSpecialConditions) {
      onSpecialConditionsUpdated([]);
    }
  };

  const onSaveCurrentPage = async () => {
    if (!contractId) return;

    if (contractDateDirty || salePriceDirty) {
      const request: StaffUpdateContractComplianceFieldsRequest = {
        contractDate: toComplianceContractDateRequest(contractDate),
        salePrice,
      };

      const response = await staffUpdateContractComplianceFields({
        contractId,
        body: request,
      });

      if (response?.success) {
        await invalidateContractDates(campaignId, contractId);
        await invalidateSaleInformation(campaignId, contractId);
        setContractDate(dateOnlyToDate(response.contractDate) ?? undefined);
        setSalePrice(response.salePrice);
        setContractDateDirty(false);
        setSalePriceDirty(false);
      }
    }

    if (
      waiverOfCoolingOffDirty ||
      subjectToFinanceDirty ||
      buildingAndPestConditionDirty ||
      bodyCorporateRecordsConditionDirty
    ) {
      const request: StaffUpdateContractDatesRequest = {
        contractDate: toContractDatesContractDateRequest(contractDate),
        coolingOffDetails: contractDates?.coolingOffDetails,
        waiverOfCoolingOff,
        subjectToFinance,
        buildingAndPestCondition,
        bodyCorporateRecordsCondition,
      };

      const response = await staffUpdateContractDates({
        contractId,
        body: request,
      });

      if (response?.success) {
        await invalidateContractDates(campaignId, contractId);
        setWaiverOfCoolingOffDirty(false);
        setSubjectToFinanceDirty(false);
        setBuildingAndPestConditionDirty(false);
        setBodyCorporateRecordsConditionDirty(false);
      }
    }

    if (settlementInfoDirty) {
      const request: StaffUpdateContractSettlementRequest = {
        settlementInfo,
        signboardRemoval: contractSettlement?.signboardRemoval,
      };

      const response = await staffUpdateContractSettlement({
        campaignId,
        contractId,
        body: request,
      });

      if (response?.success) {
        await invalidateContractSettlement(campaignId, contractId);
        setSettlementInfoDirty(false);
      }
    }

    if (specialConditionsDirtyRef.current) {
      const preparedSpecialConditions = prepareCustomSpecialConditionsForSave(
        specialConditionsRef.current
      );
      const response = await staffUpdateContractSpecialConditions({
        contractId,
        body: preparedSpecialConditions,
      });

      if (!response?.success) {
        throw new Error(response?.errors?.join(', ') || 'Special conditions were not valid');
      }

      await invalidateSpecialConditions(campaignId, contractId);
      setSpecialConditions(preparedSpecialConditions);
      specialConditionsRef.current = preparedSpecialConditions;
      setSpecialConditionsDirty(false);
      specialConditionsDirtyRef.current = false;
    }

    if (depositsDirty) {
      const response = await staffUpdateContractDeposits({
        contractId,
        body: {
          deposits: [initialDeposit, balanceDeposit].filter(Boolean) as Deposit[],
        },
      });

      if (response?.success) {
        await invalidateContractDeposits(campaignId, contractId);
        setDepositsDirty(false);
      }
    }
  };

  const currentContractDate = contractDateDirty
    ? contractDate
    : (contractDate ?? dateOnlyToDate(contractDates?.contractDate) ?? undefined);
  const currentSalePrice = salePriceDirty ? salePrice : (salePrice ?? saleInformation?.salePrice);
  const currentSubjectToFinance = subjectToFinanceDirty
    ? subjectToFinance
    : (subjectToFinance ?? contractDates?.subjectToFinance);
  const currentWaiverOfCoolingOff = waiverOfCoolingOffDirty
    ? waiverOfCoolingOff
    : (waiverOfCoolingOff ?? contractDates?.waiverOfCoolingOff);
  const currentBuildingAndPestCondition = buildingAndPestConditionDirty
    ? buildingAndPestCondition
    : (buildingAndPestCondition ?? contractDates?.buildingAndPestCondition);
  const currentBodyCorporateRecordsCondition = bodyCorporateRecordsConditionDirty
    ? bodyCorporateRecordsCondition
    : (bodyCorporateRecordsCondition ?? contractDates?.bodyCorporateRecordsCondition);
  const currentSettlementInfo = settlementInfoDirty
    ? settlementInfo
    : (settlementInfo ?? contractSettlement?.settlementInfo);
  const currentInitialDeposit = depositsDirty
    ? initialDeposit
    : (initialDeposit ?? originalInitialDeposit);
  const currentBalanceDeposit = depositsDirty
    ? balanceDeposit
    : (balanceDeposit ?? originalBalanceDeposit);
  const currentSpecialConditions = specialConditions;

  const renderPage = (props: TaskModalPageProps) => (
    <ContractCompliancePage
      {...props}
      contractDate={currentContractDate}
      onContractDateUpdated={onContractDateUpdated}
      salePrice={currentSalePrice}
      onSalePriceUpdated={onSalePriceUpdated}
      subjectToFinance={currentSubjectToFinance}
      onSubjectToFinanceUpdated={onSubjectToFinanceUpdated}
      waiverOfCoolingOff={currentWaiverOfCoolingOff}
      onWaiverOfCoolingOffUpdated={onWaiverOfCoolingOffUpdated}
      buildingAndPestCondition={currentBuildingAndPestCondition}
      onBuildingAndPestConditionUpdated={onBuildingAndPestConditionUpdated}
      bodyCorporateRecordsCondition={currentBodyCorporateRecordsCondition}
      onBodyCorporateRecordsConditionUpdated={onBodyCorporateRecordsConditionUpdated}
      settlementInfo={currentSettlementInfo}
      onSettlementInfoUpdated={onSettlementInfoUpdated}
      initialDeposit={currentInitialDeposit}
      balanceDeposit={currentBalanceDeposit}
      onDepositsUpdated={onDepositsUpdated}
      hasSpecialConditions={hasSpecialConditions}
      onHasSpecialConditionsUpdated={onHasSpecialConditionsUpdated}
      specialConditions={currentSpecialConditions}
      onSpecialConditionsUpdated={onSpecialConditionsUpdated}
    />
  );

  // const isPageInvalid = (
  //   (task: AutopilotTaskDto) => {
  //     const isGeneralComplianceTask =
  //       task.category?.taskType ===
  //       TaskCategoryTaskType.GeneralContractCompliance

  //     const requiresContractDate = Boolean(
  //       isGeneralComplianceTask &&
  //       task.taskSteps?.some(
  //         (step) => step.name === REQUIRED_CONTRACT_DATE_STEP_NAME
  //       )
  //     )
  //     const requiresSalePrice = Boolean(
  //       isGeneralComplianceTask &&
  //       task.taskSteps?.some(
  //         (step) => step.name === REQUIRED_SALE_PRICE_STEP_NAME
  //       )
  //     )
  //     const requiresInitialDeposit = Boolean(
  //       task.taskSteps?.some((step) => step.name === INITIAL_DEPOSIT_STEP_NAME)
  //     )
  //     const requiresBalanceDeposit = Boolean(
  //       task.taskSteps?.some((step) => step.name === BALANCE_DEPOSIT_STEP_NAME)
  //     )
  //     const requiresFinanceCondition = Boolean(
  //       task.taskSteps?.some((step) => step.name === FINANCE_CONDITION_STEP_NAME)
  //     )
  //     const requiresBuildingAndPest = Boolean(
  //       task.taskSteps?.some((step) => step.name === BUILDING_AND_PEST_STEP_NAME)
  //     )
  //     const requiresBodyCorporateRecords = Boolean(
  //       task.taskSteps?.some(
  //         (step) => step.name === BODY_CORPORATE_RECORDS_STEP_NAME
  //       )
  //     )
  //     const requiresSettlementDate = Boolean(
  //       task.taskSteps?.some((step) => step.name === SETTLEMENT_DATE_STEP_NAME)
  //     )
  //     const requiresSpecialConditionsAddressed = Boolean(
  //       task.taskSteps?.some((step) =>
  //         isSpecialConditionsComplianceStep(step.name)
  //       )
  //     )

  //     return (
  //       (requiresContractDate && !currentContractDate) ||
  //       (requiresSalePrice && currentSalePrice == null) ||
  //       (requiresInitialDeposit &&
  //         depositValidationError) ||
  //       (requiresBalanceDeposit &&
  //         depositValidationError) ||
  //       (requiresFinanceCondition &&
  //         financeConditionValidationError) ||
  //       (requiresBuildingAndPest &&
  //         buildingAndPestValidationError) ||
  //       (requiresBodyCorporateRecords &&
  //         bodyCorporateRecordsValidationError) ||
  //       (requiresSettlementDate &&
  //         settlementDateValidationError) ||
  //       (requiresSpecialConditionsAddressed &&
  //         hasSpecialConditions &&
  //         !currentSpecialConditionsValid)
  //     )
  //   },
  //   [
  //     currentContractDate,
  //     currentInitialDeposit,
  //     currentBalanceDeposit,
  //     depositValidationError,
  //     financeConditionValidationError,
  //     buildingAndPestValidationError,
  //     bodyCorporateRecordsValidationError,
  //     settlementDateValidationError,
  //     currentBodyCorporateRecordsCondition,
  //     currentBuildingAndPestCondition,
  //     currentSalePrice,
  //     currentSettlementInfo,
  //     currentSubjectToFinance,
  //     hasSpecialConditions,
  //     currentSpecialConditionsValid,
  //   ]
  // )

  if (!campaign) return null;

  const contentConfig: TaskModalContentConfig = {
    documents: filteredDocuments,
    isPending:
      isCampaignPending ||
      contractDatesPending ||
      contractSettlementPending ||
      contractDepositsPending ||
      saleInformationPending ||
      specialConditionsPending ||
      specialConditionBankPending,
    onSaveAllPages: async () => {
      await onSaveCurrentPage();
    },
    renderPage,
    onCompleteSuccess,
    modalId: 'contract-compliance-task-modal',
  };

  return (
    <TaskModalDialog
      title="Contract Compliance"
      tasks={tasks}
      open={isOpen}
      onClose={onClose}
      campaignId={campaignId}
      campaign={campaign}
      contentConfig={contentConfig}
    />
  );
};

export default ContractComplianceModal;
